import { useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetDevicesQuery } from 'src/api/device';
import { useGetInteractionsQuery } from 'src/api/interactions';
import { useGetMyBrandsQuery } from 'src/api/branding';
import {
  useEditCampaignSlotMutation,
  useGetCampaignQuery,
} from 'src/api/campaigns/hooks';
import { useGlobalModals } from 'src/contexts/global-modals';
import {
  validateSelect,
  validateEmpty,
  validateDateRange,
} from 'src/helpers/validators';

export interface FormValues {
  name: string;
  dateFrom: Date | null;
  dateTo: Date | null;
  deviceId: string;
  interactionId: string;
  brandingId: string;
}

const validation = {
  name: (value: string) => validateEmpty(value, 'Name is required'),
  deviceId: (value: string) => validateEmpty(value, 'Device is required'),
  interactionId: (value: string) =>
    validateSelect(value, 'Interaction is required'),
  brandingId: (value: string) => validateSelect(value, 'Branding is required'),
  dateFrom: (value: Date | null) =>
    validateEmpty(value ? value.toString() : '', 'Start date is required'),
  dateTo: (value: Date | null, allValues: any) =>
    validateDateRange(
      value,
      allValues.dateFrom,
      'End date is required',
      'End date must be after start date'
    ),
};

export const useEditSlotModal = () => {
  const { id: campaignIdParam, slotId: slotIdParam } = useParams<{
    id: string;
    slotId: string;
  }>();

  const campaignId = Number(campaignIdParam);
  const slotId = Number(slotIdParam);

  const navigate = useNavigate();
  const { data: devices } = useGetDevicesQuery();
  const { data: interactions } = useGetInteractionsQuery();
  const { data: brandings } = useGetMyBrandsQuery();
  const { data: campaign, isLoading } = useGetCampaignQuery(campaignId);
  const { editCampaignSlotMutation, isPending } = useEditCampaignSlotMutation();
  const { openLoadingModal, closeLoadingModal } = useGlobalModals();
  const { openDeleteCampaignSlotModal } = useGlobalModals();

  const minDate = campaign?.date_from;
  const maxDate = campaign?.date_to;

  const slot = campaign?.slots?.find((s) => s.id === Number(slotId));

  const handleDelete = useCallback(() => {
    openDeleteCampaignSlotModal(slotId);
    navigate(`/campaign/${campaignId}`);
  }, [openDeleteCampaignSlotModal, slotId, navigate, campaignId]);

  const handleSave = useCallback(
    async (data: FormValues) => {
      if (!data.dateFrom || !data.dateTo) return;

      openLoadingModal(
        'Updating campaign slot',
        'Please wait while we update the campaign slot...'
      );

      const dateFrom = new Date(data.dateFrom).toISOString();
      const dateTo = new Date(data.dateTo).toISOString();

      try {
        await editCampaignSlotMutation({
          id: slotId,
          data: {
            name: data.name,
            date_from: dateFrom,
            date_to: dateTo,
            device_id: parseInt(data.deviceId),
            interaction_id: parseInt(data.interactionId),
            branding_id: parseInt(data.brandingId),
          },
        });
        navigate(`/campaign/${campaignId}`);
      } finally {
        closeLoadingModal();
      }
    },
    [
      slotId,
      editCampaignSlotMutation,
      openLoadingModal,
      closeLoadingModal,
      navigate,
      campaignId,
    ]
  );

  const handleClose = useCallback(() => {
    if (!isPending) {
      navigate(`/campaign/${campaignId}`);
    }
  }, [isPending, navigate, campaignId]);

  return {
    devices: devices || [],
    interactions: interactions || [],
    brandings: brandings || [],
    isPending,
    isLoading,
    slot,
    handleSave,
    handleClose,
    handleDelete,
    validation,
    minDate,
    maxDate,
  };
};
